import type {
  ApiAppointmentResponse,
  SimplifiedAppointment,
} from '@/types/appointment';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Helper function to transform API appointment data to simplified format
const transformAppointmentsToSimplified = (
  appointments: ApiAppointmentResponse[]
): SimplifiedAppointment[] => {
  return appointments.map((appointment) => {
    // Combine first and last name for patientName
    const patientName = `${appointment.firstName} ${appointment.lastName}`;

    return {
      appointmentId: String(appointment.appointmentId),
      patientId: String(appointment.patientId),
      patientName,
      patientFirstName: appointment.firstName,
      patientLastName: appointment.lastName,
      serviceName: appointment.serviceName,
      service: appointment.serviceName, // Backward compatibility alias
      providerId: String(appointment.providerId),
      cptCode: appointment.cptCode,
      startTime: appointment.appointmentStartTime,
      patientBirthDate: appointment.patientBirthday,
      patientGender: appointment.gender,
      status: appointment.status || 'PENDING', // Default status since not provided in new format
      doctorId: String(appointment.providerId), // Backward compatibility alias
      code: appointment.cptCode, // Backward compatibility alias
      scribeJobId: appointment?.scribeJobId,
    };
  });
};

// Interface for simple location response (no longer FHIR format)
export interface UserLocation {
  locationId: string;
  locationName: string;
}

interface DataState {
  appointments: {
    data: SimplifiedAppointment[] | null;
    selectedAppointment: SimplifiedAppointment | null;
    loading: boolean;
    error: string | null;
  };
  userLocations: {
    data: UserLocation[] | null;
    loading: boolean;
    error: string | null;
    fetched: boolean; // Track if locations have been fetched
  };
  selectedLocation: {
    locationId: string | null;
    locationName: string | null;
  };
  cache: {
    [key: string]: {
      data: unknown;
      timestamp: number;
      ttl: number; // Time to live in milliseconds
    };
  };
}

const initialState: DataState = {
  appointments: {
    data: null,
    selectedAppointment: null,
    loading: false,
    error: null,
  },
  userLocations: {
    data: null,
    loading: false,
    error: null,
    fetched: false,
  },
  selectedLocation: {
    locationId: null,
    locationName: null,
  },
  cache: {},
};

const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    // Appointments actions
    setAppointmentsLoading: (state, action: PayloadAction<boolean>) => {
      state.appointments.loading = action.payload;
    },
    setAppointmentsData: (
      state,
      action: PayloadAction<ApiAppointmentResponse[]>
    ) => {
      // Transform the API appointment data to simplified format
      const simplifiedData = transformAppointmentsToSimplified(action.payload);
      state.appointments.data = simplifiedData;
      state.appointments.loading = false;
      state.appointments.error = null;
    },
    setAppointmentsError: (state, action: PayloadAction<string>) => {
      state.appointments.error = action.payload;
      state.appointments.loading = false;
    },
    clearAppointments: (state) => {
      state.appointments.data = null;
      state.appointments.loading = false;
      state.appointments.error = null;
    },
    setSelectedAppointment: (
      state,
      action: PayloadAction<SimplifiedAppointment>
    ) => {
      state.appointments.selectedAppointment = action.payload;
    },
    clearSelectedAppointment: (state) => {
      state.appointments.selectedAppointment = null;
    },
    updateAppointmentScribeJobId: (
      state,
      action: PayloadAction<{
        appointmentId: string;
        scribeJobId: string;
      }>
    ) => {
      const { appointmentId, scribeJobId } = action.payload;

      // Update in appointments data array
      if (state.appointments.data) {
        const appointmentIndex = state.appointments.data.findIndex(
          (appointment) => appointment.appointmentId === appointmentId
        );
        if (appointmentIndex !== -1) {
          state.appointments.data[appointmentIndex] = {
            ...state.appointments.data[appointmentIndex],
            scribeJobId,
          };
        }
      }

      // Update in selected appointment if it matches
      if (
        state.appointments.selectedAppointment?.appointmentId === appointmentId
      ) {
        state.appointments.selectedAppointment = {
          ...state.appointments.selectedAppointment,
          scribeJobId,
        };
      }
    },
    updateAppointmentAudioUrl: (
      state,
      action: PayloadAction<{
        appointmentId: string;
        audioUrl: string;
        transcriptUrl: string;
      }>
    ) => {
      const { appointmentId, audioUrl, transcriptUrl } = action.payload;

      // Update in appointments data array
      if (state.appointments.data) {
        const appointmentIndex = state.appointments.data.findIndex(
          (appointment) => appointment.appointmentId === appointmentId
        );
        if (appointmentIndex !== -1) {
          state.appointments.data[appointmentIndex] = {
            ...state.appointments.data[appointmentIndex],
            audioUrl,
            transcriptUrl,
          };
        }
      }

      // Update in selected appointment if it matches
      if (
        state.appointments.selectedAppointment?.appointmentId === appointmentId
      ) {
        state.appointments.selectedAppointment = {
          ...state.appointments.selectedAppointment,
          audioUrl,
        };
      }
    },

    // User Locations actions
    setUserLocationsLoading: (state, action: PayloadAction<boolean>) => {
      state.userLocations.loading = action.payload;
    },
    setUserLocationsData: (state, action: PayloadAction<UserLocation[]>) => {
      state.userLocations.data = action.payload;
      state.userLocations.loading = false;
      state.userLocations.error = null;
      state.userLocations.fetched = true;
    },
    setUserLocationsError: (state, action: PayloadAction<string>) => {
      state.userLocations.error = action.payload;
      state.userLocations.loading = false;
    },
    clearUserLocations: (state) => {
      state.userLocations.data = null;
      state.userLocations.loading = false;
      state.userLocations.error = null;
      state.userLocations.fetched = false;
    },

    // Selected Location actions
    setSelectedLocation: (
      state,
      action: PayloadAction<{ locationId: string; locationName: string }>
    ) => {
      state.selectedLocation = action.payload;
    },
    clearSelectedLocation: (state) => {
      state.selectedLocation = { locationId: null, locationName: null };
    },

    // Cache actions
    setCacheData: (
      state,
      action: PayloadAction<{
        key: string;
        data: unknown;
        ttl?: number;
      }>
    ) => {
      const { key, data, ttl = 5 * 60 * 1000 } = action.payload; // Default 5 minutes
      state.cache[key] = {
        data,
        timestamp: Date.now(),
        ttl,
      };
    },
    clearCache: (state, action: PayloadAction<string>) => {
      delete state.cache[action.payload];
    },
    clearExpiredCache: (state) => {
      const now = Date.now();
      Object.keys(state.cache).forEach((key) => {
        const cacheEntry = state.cache[key];
        if (now - cacheEntry.timestamp > cacheEntry.ttl) {
          delete state.cache[key];
        }
      });
    },

    // Clear all data (for logout)
    clearAllData: (state) => {
      return initialState;
    },
  },
});

export const {
  setAppointmentsLoading,
  setAppointmentsData,
  setAppointmentsError,
  clearAppointments,
  setSelectedAppointment,
  clearSelectedAppointment,
  updateAppointmentScribeJobId,
  updateAppointmentAudioUrl,
  setUserLocationsLoading,
  setUserLocationsData,
  setUserLocationsError,
  clearUserLocations,
  setSelectedLocation,
  clearSelectedLocation,
  setCacheData,
  clearCache,
  clearExpiredCache,
  clearAllData,
} = dataSlice.actions;

export default dataSlice.reducer;
